import { Injectable, signal } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { UserRequest, UserRequestResponse } from '../../../models/user-request.model';
import { ApiService } from '../../../core/services/api.service';
import { AuthService } from '../../../core/services/auth.service';

interface PaginatedResponse<T> {
    success: boolean;
    data: {
        requests: T[];
        pagination: {
            currentPage: number;
            totalPages: number;
            totalCount: number;
            hasNext: boolean;
            hasPrev: boolean;
        };
    };
}

interface ConnectionStatusResponse {
    success: boolean;
    data: {
        status: 'none' | 'pending' | 'accepted' | 'rejected';
        requestId: string | null;
        canSendRequest: boolean;
        isConnected: boolean;
    };
}

@Injectable({
    providedIn: 'root'
})
export class UserRequestsService {
    userRequests = signal<UserRequest[]>([]);
    sentRequests = signal<UserRequest[]>([]);
    isLoading = signal<boolean>(false);

    private requestsSubject = new BehaviorSubject<UserRequest[]>([]);
    private sentRequestsSubject = new BehaviorSubject<UserRequest[]>([]);
    public requests$ = this.requestsSubject.asObservable();
    public sentRequests$ = this.sentRequestsSubject.asObservable();

    constructor(private apiService: ApiService, private authService: AuthService) { }

    getUserRequests(page: number = 1, limit: number = 20, status: string = 'pending', type: string = 'incoming'): Observable<UserRequest[]> {
        this.isLoading.set(true);
        const token = this.authService.getToken();
        const url = `/connection-requests/request/`;

        return this.apiService.post<PaginatedResponse<UserRequest>>(url, { page, limit, status, token: token, type }, true)
            .pipe(
                map(response => response.data.requests),
                tap(requests => {
                    this.userRequests.set(requests);
                    this.requestsSubject.next(requests);
                    this.isLoading.set(false);
                }),
                catchError(error => {
                    this.isLoading.set(false);
                    console.error('Error fetching user requests:', error);
                    return of([]);
                })
            );
    }

    getSentRequests(page: number = 1, limit: number = 20, status: string = 'pending'): Observable<UserRequest[]> {
        this.isLoading.set(true);
        const token = this.authService.getToken();
        const url = `/connection-requests/request`;

        return this.apiService
            .post<PaginatedResponse<UserRequest>>(url, { page, limit, status, type: 'outgoing', token }, true)
            .pipe(
                map(response => response.data.requests),
                tap(requests => {
                    this.sentRequests.set(requests);
                    this.sentRequestsSubject.next(requests);
                    this.isLoading.set(false);
                }),
                catchError(error => {
                    this.isLoading.set(false);
                    console.error('Error fetching sent requests:', error);
                    return of([]);
                })
            );
    }

    sendConnectionRequest(toUserId: string, message?: string): Observable<UserRequestResponse> {
        const token = this.authService.getToken();
        return this.apiService
            .post<{ success: boolean; message: string; data: UserRequest }>(
                '/connection-requests/send',
                { toUserId, message, token },
                true
            )
            .pipe(
                map(response => ({
                    success: response.success,
                    message: response.message,
                    request: response.data
                })),
                tap(response => {
                    if (response.success && response.request) {
                        this.addSentRequest(response.request);
                    }
                }),
                catchError(error => {
                    console.error('Error sending connection request:', error);
                    return of({
                        success: false,
                        message: error.error?.message || 'Failed to send connection request'
                    });
                })
            );
    }

    acceptRequest(requestId: string): Observable<UserRequestResponse> {
        const token = this.authService.getToken();
        return this.apiService
            .put<{ success: boolean; message: string; data: { request: UserRequest; conversationId: string } }>(
                `/connection-requests/${requestId}/accept`,
                { token },
                true
            )
            .pipe(
                map(response => ({
                    success: response.success,
                    message: response.message,
                    request: response.data.request,
                    conversationId: response.data.conversationId
                })),
                tap(response => {
                    if (response.success && response.request) {
                        this.updateRequestStatus(requestId, 'accepted');
                    }
                }),
                catchError(error => {
                    console.error('Error accepting connection request:', error);
                    return of({
                        success: false,
                        message: error.error?.message || 'Failed to accept connection request'
                    });
                })
            );
    }

    rejectRequest(requestId: string): Observable<UserRequestResponse> {
        const token = this.authService.getToken();
        return this.apiService
            .put<{ success: boolean; message: string; data: UserRequest }>(
                `/connection-requests/${requestId}/reject`,
                { token },
                true
            )
            .pipe(
                map(response => ({
                    success: response.success,
                    message: response.message,
                    request: response.data
                })),
                tap(response => {
                    if (response.success && response.request) {
                        this.updateRequestStatus(requestId, 'rejected');
                    }
                }),
                catchError(error => {
                    console.error('Error rejecting connection request:', error);
                    return of({
                        success: false,
                        message: error.error?.message || 'Failed to reject connection request'
                    });
                })
            );
    }

    cancelRequest(requestId: string): Observable<{ success: boolean; message: string }> {
        const token = this.authService.getToken();
        return this.apiService
            .delete<{ success: boolean; message: string }>(`/connection-requests/${requestId}`, { token }, true)
            .pipe(
                tap(response => {
                    if (response.success) {
                        this.removeSentRequest(requestId);
                    }
                }),
                catchError(error => {
                    console.error('Error cancelling connection request:', error);
                    return of({
                        success: false,
                        message: error.error?.message || 'Failed to cancel connection request'
                    });
                })
            );
    }

    getConnectionStatus(userId: string): Observable<ConnectionStatusResponse['data']> {
        const token = this.authService.getToken();
        return this.apiService
            .post<ConnectionStatusResponse>(`/connection-requests/status`, { userId, token }, true)
            .pipe(
                map(response => response.data),
                catchError(error => {
                    console.error('Error getting connection status:', error);
                    return of({
                        status: 'none' as const,
                        requestId: null,
                        canSendRequest: true,
                        isConnected: false
                    });
                })
            );
    }

    updateRequests(requests: UserRequest[]) {
        this.userRequests.set(requests);
        this.requestsSubject.next(requests);
    }

    updateSentRequests(requests: UserRequest[]) {
        this.sentRequests.set(requests);
        this.sentRequestsSubject.next(requests);
    }

    addRequest(request: UserRequest) {
        const current = this.userRequests();
        const updated = [request, ...current];
        this.userRequests.set(updated);
        this.requestsSubject.next(updated);
    }

    addSentRequest(request: UserRequest) {
        const current = this.sentRequests();
        const updated = [request, ...current];
        this.sentRequests.set(updated);
        this.sentRequestsSubject.next(updated);
    }

    updateRequestStatus(requestId: string, status: 'accepted' | 'rejected') {
        const currentIncoming = this.userRequests();
        const updatedIncoming = currentIncoming.map(request =>
            request._id === requestId ? { ...request, status, updatedAt: new Date().toISOString() } : request
        );
        this.userRequests.set(updatedIncoming);
        this.requestsSubject.next(updatedIncoming);

        const currentSent = this.sentRequests();
        const updatedSent = currentSent.map(request =>
            request._id === requestId ? { ...request, status, updatedAt: new Date().toISOString() } : request
        );
        this.sentRequests.set(updatedSent);
        this.sentRequestsSubject.next(updatedSent);
    }

    removeRequest(requestId: string) {
        const current = this.userRequests();
        const filtered = current.filter(request => request._id !== requestId);
        this.userRequests.set(filtered);
        this.requestsSubject.next(filtered);
    }

    removeSentRequest(requestId: string) {
        const current = this.sentRequests();
        const filtered = current.filter(request => request._id !== requestId);
        this.sentRequests.set(filtered);
        this.sentRequestsSubject.next(filtered);
    }

    getPendingRequestsCount(): number {
        return this.userRequests().filter(request => request.status === 'pending').length;
    }

    getPendingSentRequestsCount(): number {
        return this.sentRequests().filter(request => request.status === 'pending').length;
    }

    hasActiveRequest(userId: string): boolean {
        return this.sentRequests().some(request => request.toUser._id === userId && request.status === 'pending');
    }

    hasReceivedRequestFrom(userId: string): boolean {
        return this.userRequests().some(request => request.fromUser._id === userId && request.status === 'pending');
    }

    isConnectedWith(userId: string): boolean {
        return (
            this.userRequests().some(request => request.fromUser._id === userId && request.status === 'accepted') ||
            this.sentRequests().some(request => request.toUser._id === userId && request.status === 'accepted')
        );
    }

    getRequestsForUser(userId: string): { incoming: UserRequest | null; sent: UserRequest | null } {
        const incoming = this.userRequests().find(request => request.fromUser._id === userId) || null;
        const sent = this.sentRequests().find(request => request.toUser._id === userId) || null;
        return { incoming, sent };
    }

    clearAllRequests() {
        this.userRequests.set([]);
        this.sentRequests.set([]);
        this.requestsSubject.next([]);
        this.sentRequestsSubject.next([]);
    }

    refreshAllRequests(): Observable<{ incoming: UserRequest[]; sent: UserRequest[] }> {
        this.isLoading.set(true);

        const incoming$ = this.getUserRequests(1, 50, 'all');
        const sent$ = this.getSentRequests(1, 50, 'all');

        return new Observable(observer => {
            Promise.all([incoming$.toPromise(), sent$.toPromise()])
                .then(([incoming, sent]) => {
                    this.isLoading.set(false);
                    observer.next({
                        incoming: incoming || [],
                        sent: sent || []
                    });
                    observer.complete();
                })
                .catch(error => {
                    this.isLoading.set(false);
                    console.error('Error refreshing requests:', error);
                    observer.error(error);
                });
        });
    }

    // ==== Socket Handlers ====

    handleSocketConnectionRequest(request: UserRequest) {
        this.addRequest(request);
    }

    handleSocketConnectionAccepted(data: { request: UserRequest; conversationId: string }) {
        this.updateRequestStatus(data.request._id, 'accepted');
    }

    handleSocketConnectionRejected(request: UserRequest) {
        this.updateRequestStatus(request._id, 'rejected');
    }
}